import React, { useRef, useState } from "react";
import { useEditorStore } from "@state/editor";
import { useThemeStore } from "@state/theme";
import { useAuthStore } from "@state/auth";
import { apiClient } from "@api/index";
import EventCard from "./EventCard";
import { getCardWidth } from "@utils/sizingUtils";

interface BasicCarouselPreviewComponentProps {
  title: string;
  noPadding?: boolean;
  isPortrait?: boolean;
  categoryIDs?: string[];
  isPersistent?: boolean;
  isMostWatched?: boolean;
  persistentStreams?: any[];
  eventStatus?: string;
  keywords?: string[];
  startDate?: Date | null;
  endDate?: Date | null;
  showSeeMore?: boolean;
  actionButtonText?: string;
  connect?: any;
  drag?: any;
}

export const BasicCarouselPreviewComponent: React.FC<BasicCarouselPreviewComponentProps> = ({
  title,
  noPadding,
  isPortrait,
  categoryIDs,
  isPersistent,
  isMostWatched,
  persistentStreams,
  eventStatus,
  keywords,
  startDate,
  endDate,
  showSeeMore,
  actionButtonText,
  connect,
  drag,
}) => {
  const user = useAuthStore((state) => state.user);
  const { textColor, accentColor } = useThemeStore();

  // Dummy events for loading state
  const dummyEvents = Array.from({ length: 7 }, (_, i) => ({
    eventID: `event-${i}`,
    clientID: "1000000000",
    title: "Event Title",
    eventStatus: "new",
    description: "Event Description",
    startDateTime: new Date(),
  }));

  const [events, setEvents] = useState(dummyEvents);

  // Fetch standard events only when not in persistent mode
  const { isLoading, error } = apiClient.event.getFilteredEvents.useQuery(
    {
      clientID: user.clientID as string,
      anyCategories: categoryIDs && categoryIDs.length > 0 ? categoryIDs : undefined,
      eventStatus: eventStatus || undefined,
      keywords: keywords && keywords.length > 0 ? keywords : undefined,
      startDate: startDate ? new Date(startDate).toISOString() : undefined,
      endDate: endDate ? new Date(endDate).toISOString() : undefined,
      includePrivate: true,
    },
    {
      enabled: !isPersistent || !isMostWatched,
      onSuccess(data) {
        setEvents(data as any);
      },
    },
  );

  // Fetch persistent streams when persistent mode is enabled
  const { isLoading: isPersistentLoading } = apiClient.event.getPersistentStreams.useQuery(
    { clientID: user.clientID as string, getThumb: true },
    {
      refetchOnWindowFocus: false,
      enabled: isPersistent,
      onSuccess(data) {
        setEvents(
          data.map((stream) => ({
            clientID: "10000",
            description: stream.description,
            eventID: stream.encoderID,
            eventStatus: "247",
            startDateTime: new Date(stream.lastCommunication),
            title: stream.title,
            videoThumbnail: stream.thumbImage,
          }))
        );
      },
    },
  );

  const { isLoading: isMostWatchedLoading } = apiClient.event.getMostWatchedEvents.useQuery(
    {clientID: user.clientID as string},
    {
      refetchOnWindowFocus: false,
      enabled: isMostWatched,
      onSuccess(data) {
        // setEvents(data as any);
        setEvents(
          data.map((mostWatchedEvent) => ({
            clientID: user.clientID,
            eventID: mostWatchedEvent.eventID,
            description: mostWatchedEvent.description,
            eventStatus: mostWatchedEvent.eventStatus,
            videoThumbnail: mostWatchedEvent.image,
            startDateTime: new Date(mostWatchedEvent.startDateTime),
            title: mostWatchedEvent.title,
          }) as any)
        )
      }
    }
  )

  const carouselRef = useRef<HTMLDivElement>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [carouselItemWidth, setCarouselItemWidth] = useState(0);

  const handlePrevClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (carouselRef.current) {
      const firstChild = carouselRef.current.firstChild;
      const firstChildFirstChild = firstChild?.firstChild as HTMLElement | null;
      if (firstChildFirstChild) {
        setCarouselItemWidth(firstChildFirstChild.getBoundingClientRect().width);
      }
    }
    setCurrentIndex((prev) => prev - 1);
  };

  const handleNextClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (carouselRef.current) {
      const firstChild = carouselRef.current.firstChild;
      const firstChildFirstChild = firstChild?.firstChild as HTMLElement | null;
      if (firstChildFirstChild) {
        setCarouselItemWidth(firstChildFirstChild.getBoundingClientRect().width);
      }
    }
    setCurrentIndex((prev) => prev + 1);
  };

  const handleMouseEnter = () => setIsHovering(true);
  const handleMouseLeave = () => setIsHovering(false);

  if (isLoading || isPersistentLoading || isMostWatchedLoading) {
    return (
      <div
        ref={(ref: any) => (connect && drag ? connect(drag(ref)) : undefined)}
        className={`relative ${noPadding ? "" : "pt-4 pb-6"}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="flex flex-row ml-4 pb-1 items-center gap-4">
          <h2
            className="mb-1 text-lg tracking-tight sm:text-xl md:text-xl font-bold"
            style={{ color: textColor }}
          >
            {title}
          </h2>
        </div>
        <div ref={carouselRef} className="w-full relative overflow-hidden">
          <div
            className="flex ml-4 hide-scrollbar snap-x snap-mandatory flex-row items-center transition-all duration-700"
            style={{
              transform: `translateX(-${currentIndex * carouselItemWidth}px)`,
            }}
          >
            {dummyEvents.map((ev) => (
              <EventLoadingCard key={ev.eventID} isPortrait={isPortrait} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <div>Error fetching events</div>;
  }

  return (
    <div
      ref={(ref: any) => (connect && drag ? connect(drag(ref)) : undefined)}
      className={`relative ${noPadding ? "" : "pt-4 pb-6"}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex flex-row ml-4 pb-1 items-center gap-4">
        <h2
          className="mb-1 text-lg tracking-tight sm:text-xl md:text-xl font-bold"
          style={{ color: textColor }}
        >
          {title}
        </h2>
        {showSeeMore && !isMostWatched && (
          <div
            className="flex flex-row items-center font-semibold cursor-pointer"
            style={{ color: accentColor }}
          >
            <p>{actionButtonText || "See More"}</p>
            <svg
              className="w-6 h-6"
              viewBox="0 0 24 24"
              height="24"
              width="24"
              role="img"
              aria-hidden="true"
            >
              <title>Link Arrow</title>
              <path
                stroke="currentColor"
                strokeWidth="2"
                d="M9.5 17.5l5-5-5-5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
            </svg>
          </div>
        )}
      </div>

      <div ref={carouselRef} className="w-full relative overflow-hidden">
        <div
          className="flex ml-4 hide-scrollbar snap-x snap-mandatory flex-row items-center transition-all duration-700"
          style={{
            transform: `translateX(-${currentIndex * carouselItemWidth}px)`,
          }}
        >
          {events.length > 0 ? (
            <>
              {events.map((event) => {
                // If in persistent mode, you can further filter based on your persistentStreams logic
                if (
                  isPersistent &&
                  persistentStreams &&
                  !persistentStreams.includes(event.eventID)
                ) {
                  return null;
                }
                return (
                  <EventCard
                    key={event.eventID}
                    event={event as any}
                    isPortrait={isPortrait}
                  />
                );
              })}
            </>
          ) : (
            <>
              {dummyEvents.map((ev) => (
                <EventLoadingCard key={ev.eventID} isPortrait={isPortrait} />
              ))}
            </>
          )}
        </div>

        {isHovering && currentIndex !== 0 && (
          <button
            className="absolute h-full left-0 top-1/2 transform -translate-y-1/2 p-3"
            style={{ color: accentColor }}
            onClick={handlePrevClick}
          >
            <svg viewBox="0 0 24 24" className="w-8 h-8 fill-current">
              <path d="M15.4 4.6a1 1 0 0 0-1.41 0L6.7 11.3a1 1 0 0 0 0 1.4l7.3 7.3a1 1 0 1 0 1.4-1.4L9.8 12l5.6-5.7a1 1 0 0 0 0-1.4z"></path>
            </svg>
          </button>
        )}
        {isHovering && currentIndex < events.length - 1 && (
          <button
            className="absolute h-full right-0 top-1/2 transform -translate-y-1/2 p-3"
            style={{ color: accentColor }}
            onClick={handleNextClick}
          >
            <svg viewBox="0 0 24 24" className="w-8 h-8 fill-current">
              <path d="M8.6 4.6a1 1 0 0 1 1.4 0l7.3 7.3a1 1 0 0 1 0 1.4l-7.3 7.3a1 1 0 1 1-1.4-1.4L14.2 12 8.6 6.3a1 1 0 0 1 0-1.4z"></path>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

interface EventLoadingCardProps {
  isPortrait?: boolean;
}

const EventLoadingCard: React.FC<EventLoadingCardProps> = ({ isPortrait }) => {
  const renderPanelWidth = useEditorStore(({ renderPanelSize }) => renderPanelSize.width);
  const cardWidth = getCardWidth(renderPanelWidth || 0, isPortrait);
  return (
    <div className={`event-card-loading flex flex-col mr-4 my-1 animate-pulse rounded-lg md:rounded-xl`}>
      <div
        className={`relative w-full flex flex-col justify-between rounded-lg md:rounded-xl bg-gray-400`}
        style={{
          width: cardWidth,
          height: isPortrait ? cardWidth * 1.5 : cardWidth / 1.5,
        }}
      >
        <div className={`${isPortrait ? "aspect-portrait" : "aspect-video"} bg-gray-400 rounded-lg md:rounded-xl`}></div>
        <div className={`rounded-b-lg md:rounded-b-xl ${isPortrait ? "absolute bottom-0 left-0 right-0 pl-3 pb-3" : "pl-1 pb-1"}`}></div>
      </div>
    </div>
  );
};
